import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { Checkbox, Form, Input, Modal, Select, Spin } from 'antd';

import ApiConnectionsContext from '@totem/components/administration/apiConnections/ApiConnectionsContext';
import TemperedConductorFields from '@totem/components/administration/apiConnections/TemperedConductorFields';
import {
  API_CONNECTION_TYPES,
  ApiConnection,
  ObjectReference,
} from '@totem/components/administration/apiConnections/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const EMPTY_ID = '000000000000000000000000';
const EMPTY_REF: ObjectReference = { id: '', name: '' };

const PASSWORD_KEYS = new Set(['token', 'clientSecret', 'password']);
const CHECKBOX_KEYS = new Set(['confirmed']);

const TYPE_OPTIONS = API_CONNECTION_TYPES.map((typ) => ({
  value: typ,
  label: typ,
}));

const ApiConnectionDialog = () => {
  const {
    editingConnection,
    editLoading,
    modalOpen,
    typeFields,
    onSave,
    onModalClose,
  } = useContext(ApiConnectionsContext);
  const { userProfile } = useContext(UserProfileContext);
  const [form] = Form.useForm();
  const selectedOrg = useRef<ObjectReference>(EMPTY_REF);

  const currentType: string = Form.useWatch('type', form);
  const resolvedType = Array.isArray(currentType)
    ? currentType[0]
    : currentType;
  // Fall back to editingConnection's own type on the first render after
  // opening — form.setFieldsValue hasn't populated "type" yet at that point
  // (it runs in a useEffect below), so relying on resolvedType alone would
  // briefly mount the generic fields, then swap to TemperedConductorFields
  // once the form catches up — unmounting/remounting the group/tag Selects
  // with no options loaded yet, which looks like the saved values were lost.
  const isTemperedConductor =
    (resolvedType || editingConnection?.type) === 'Tempered_Conductor';

  const visibleKeys = useMemo(() => {
    const match = typeFields.find((tfd) => tfd.type === resolvedType);

    if (!match) {
      return null;
    }

    return new Set(match.fields.map((fld) => fld.key));
  }, [resolvedType, typeFields]);

  const show = (key: string) => visibleKeys === null || visibleKeys.has(key);

  const orgOptions = useMemo(() => {
    const active = [...(userProfile.organizations ?? [])]
      .filter((org) => org.isActive)
      .sort((aOrg, bOrg) => sortStringAscending(aOrg.name, bOrg.name))
      .map((org) => ({ value: org.id, label: org.name }));

    return [{ value: EMPTY_ID, label: 'Unassigned' }, ...active];
  }, [userProfile.organizations]);

  useEffect(() => {
    if (modalOpen) {
      if (editingConnection) {
        selectedOrg.current = editingConnection.organization ?? EMPTY_REF;

        form.setFieldsValue({
          type: editingConnection.type,
          organizationId: editingConnection.organization?.id ?? EMPTY_ID,
          displayName: editingConnection.displayName,
          ...editingConnection.data,
        });
      } else {
        selectedOrg.current = { id: EMPTY_ID, name: '' };

        form.resetFields();

        form.setFieldValue('organizationId', EMPTY_ID);
      }
    }
  }, [modalOpen, editingConnection, form]);

  const handleOrgSelectChange = (orgId: string) => {
    if (orgId === EMPTY_ID) {
      selectedOrg.current = { id: EMPTY_ID, name: '' };
      return;
    }

    const org = userProfile.organizations?.find((item) => item.id === orgId);

    selectedOrg.current = org ? { id: org.id, name: org.name } : EMPTY_REF;
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      const { displayName, ...connectionFields } = values;

      delete connectionFields.type;

      delete connectionFields.organizationId;

      const connection: Partial<ApiConnection> = {
        ...(editingConnection?.id ? { id: editingConnection.id } : {}),
        type: resolvedType,
        displayName,
        organization: selectedOrg.current,
        data: connectionFields,
      };

      onSave(connection);
    });
  };

  const renderField = (key: string, label: string) => {
    if (!show(key)) {
      return null;
    }

    if (CHECKBOX_KEYS.has(key)) {
      return (
        <Form.Item key={key} name={key} valuePropName="checked" label={label}>
          <Checkbox />
        </Form.Item>
      );
    }

    if (PASSWORD_KEYS.has(key)) {
      return (
        <Form.Item key={key} name={key} label={label}>
          <Input.Password />
        </Form.Item>
      );
    }

    return (
      <Form.Item key={key} name={key} label={label}>
        <Input />
      </Form.Item>
    );
  };

  return (
    <Modal
      title={editingConnection ? 'Edit API Connection' : 'New API Connection'}
      open={modalOpen}
      onOk={handleOk}
      onCancel={onModalClose}
      okText={editingConnection ? 'Save' : 'Create'}
      okButtonProps={{ disabled: editLoading }}
      destroyOnClose
    >
      <Spin spinning={editLoading}>
        <Form form={form} layout="vertical">
          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: 'Type is required' }]}
          >
            <Select
              showSearch
              mode="tags"
              maxCount={1}
              placeholder="Select or enter type"
              disabled={!!editingConnection}
              options={TYPE_OPTIONS}
            />
          </Form.Item>
          <Form.Item
            name="displayName"
            label="Display Name"
            rules={[{ required: true, message: 'Display name is required' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="organizationId" label="Organization">
            <Select
              showSearch
              placeholder="Select organization"
              options={orgOptions}
              onChange={handleOrgSelectChange}
              optionFilterProp="label"
            />
          </Form.Item>
          {isTemperedConductor ? (
            <TemperedConductorFields />
          ) : (
            <>
              {renderField('baseUrl', 'Base URL')}
              {renderField('token', 'Token')}
              {renderField('clientId', 'Client ID')}
              {renderField('clientSecret', 'Client Secret')}
              {renderField('username', 'Username')}
              {renderField('password', 'Password')}
              {renderField('domain', 'Domain')}
              {renderField('audience', 'Audience')}
              {renderField('group', 'Group')}
              {renderField('tenantUUID', 'Tenant UUID')}
              {renderField('partnerId', 'Partner ID')}
              {renderField('partnerName', 'Partner Name')}
              {renderField('confirmed', 'Confirmed')}
            </>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default ApiConnectionDialog;
